/** @jsx jsx */
import React, { useEffect } from 'react';
import { jsx } from '@emotion/core';

import ActionButtonsContainer from 'components/ActionButtonsContainer';
import useTranslate, {
  useMarkdownTranslate,
  useMarkdownMultilineTranslate,
} from 'hooks/useTranslate/useTranslate';

import styles from './styles';
import { Divider } from '@material-ui/core';
import Button from 'components/Button';
import Link from 'components/Link';
import ExpandablePanel from 'components/ExpandablePanel';

import { conformToMask, moneyMask } from 'utils/masks';

import useAnalytics, { Event } from 'hooks/useAnalytics';
import useAnalyticVariables from 'hooks/useAnalyticVariables';
import BreadTitle from '../OfferIntro/BreadTitle';

interface CreditCardPaymentOptionProps {
  openToBuy?: number;
  panelName: string;
  expanded: boolean;
  handlePanelExpandChange: (
    event: React.ChangeEvent<{}>,
    panelName: string,
    expanded: boolean,
  ) => void;
}

const CreditCardExistingAccount: React.FC<
  CreditCardPaymentOptionProps
> = props => {
  const { openToBuy, panelName, expanded, handlePanelExpandChange } = props;
  const tMd = useMarkdownTranslate();
  const tMdMultiline = useMarkdownMultilineTranslate();
  const t = useTranslate();

  const { sendEvents } = useAnalytics();
  const { setAnalyticsVariables } = useAnalyticVariables();

  useEffect(() => {
    expanded && sendEvents([Event.PrequalAccountFoundExpanded]);
  }, [expanded, sendEvents, setAnalyticsVariables]);

  const title = (
    <div>
      <div css={styles.paymentOption.summaryTitle}>
        <BreadTitle
          id={'creditCardExistingAccountBreadLogo'}
          titlePart={
            <React.Fragment>
              {tMd(
                'unified.offer.paymentOption.creditCard.existingAccount.title',
              )}
              {t('unified.creditCardLogo') !== '' && (
                <img
                  src={`data:image/png;base64,${t('unified.creditCardLogo')}`}
                  alt={t('unified.creditCardLogoAlt')}
                  css={styles.paymentOption.cardIcon}
                />
              )}
            </React.Fragment>
          }
          logo={t(
            'unified.offer.paymentOption.creditCard.existingAccount.breadLogo',
          )}
        />
      </div>
      <div css={styles.paymentOption.summaryBody}>
        {/*Get <span css={styles.boldColorText}>120.00 off</span> this purchase
        when you get 20% off your first Nebella purchase with the Nebella Credit
        Card*/}
        {openToBuy
          ? tMd(
              'unified.offer.paymentOption.creditCard.existingAccount.descriptionWithOpenToBuy',
              {
                amount: conformToMask(openToBuy.toString(), moneyMask),
              },
            )
          : tMd(
              'unified.offer.paymentOption.creditCard.existingAccount.descriptionWithoutOpenToBuy',
            )}
      </div>
    </div>
  );
  const body = (
    <div css={styles.paymentOption.body}>
      <div css={styles.paymentOption.section}>
        <Divider />
      </div>
      <div css={styles.paymentOption.section}>
        {tMdMultiline(
          'unified.offer.paymentOption.creditCard.existingAccount.message',
        )}
      </div>
    </div>
  );
  const footer = (
    <React.Fragment>
      <ActionButtonsContainer>
        <Button
          mobileFullWidth={true}
          id="creditCardAcceptOffer"
          component={Link}
          to={'/existing-account'}
        >
          {tMd(
            'unified.offer.paymentOption.creditCard.existingAccount.retrievePassButton',
          )}
        </Button>
      </ActionButtonsContainer>
    </React.Fragment>
  );

  return (
    <ExpandablePanel
      title={title}
      children={body}
      footer={footer}
      panelName={panelName}
      expanded={expanded}
      onChange={handlePanelExpandChange}
      banner={
        openToBuy
          ? tMd(
              'unified.offer.paymentOption.creditCard.existingAccount.bannerWithOpenToBuy',
              {
                amount: conformToMask(openToBuy.toString(), moneyMask),
              },
            )
          : tMd(
              'unified.offer.paymentOption.creditCard.existingAccount.bannerWithoutOpenToBuy',
            )
      }
    />
  );
};

export default CreditCardExistingAccount;
